import React, { useEffect, useState, useRef } from "react";
import {
  Container,
  Header,
  Divider,
  Segment,
  Message,
  Form,
  Icon,
  Button,
} from "semantic-ui-react";
import { ajax } from "../../ajax.js";
import { authenticator } from "../../authenticator.js";
import { debugMessage, queryString, isValidEmailAddress, isStaffEmail } from "../../common";
import { getEnglishText } from "../../dictionary.js";
import {
  coerceAction,
  readStoredAction,
  writeStoredAction,
  clearStoredAction,
} from "../../lib/pendingAction";
import { hideBootSplash } from "../../lib/bootSplash";
import { chooseQuickStartStage } from "../../lib/quickStartStage";

import { phone } from "phone";
import {
  postcodeValidator,
  postcodeValidatorExistsForCountry,
} from "postcode-validator";

import "./QuickStart.css";

///////////////
// TEST MODE //
///////////////

// http://localhost/quick-start?action={"configId":"P3-0000","hash":"1234"}&stage=start
// http://localhost/quick-start?action={"configId":"P3-0000","hash":"1234"}&stage=add-product
// http://localhost/quick-start?action={"configId":"P3-0000","hash":"1234"}&stage=new-account

// QR onboarding: Cognito login → if this email has no Portal owner account,
// collect contact/address → name the unit → POST /quickStart with configId+hash.
// Existing owners skip the account form. Staff/installers are sent to Home.

function readQuickStartAction() {
  const fromQuery = coerceAction(queryString().action);
  if (fromQuery.configId || fromQuery.hash || fromQuery.action) return fromQuery;
  return readStoredAction();
}

function QuickStart() {
  const qs = queryString();
  const action = readQuickStartAction();
  const configId = action.configId;
  const hash = action.hash;

  const testMode = configId === "P3-0000" && hash === "1234";

  const userEmail = authenticator.getUserDetails()["email"];

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState();
  const [showStartEscape, setShowStartEscape] = useState(false);

  const [stage, setStage] = useState(testMode ? qs.stage || "start" : "start");
  const [existingUnits, setExistingUnits] = useState([]);

  const [newAccountForm, setNewAccountForm] = useState({
    title: "",
    firstName: "",
    lastName: "",
    addressLine1: "",
    addressLine2: "",
    addressCityTown: "",
    postcode: "",
    mobilePhoneNumber: "",
    additionalEmail1: "",
    additionalEmail2: "",
    additionalEmail3: "",
  });
  const [addProductForm, setAddProductForm] = useState({
    customName: configId,
  });

  const newAccountInputRef = useRef();
  const addProductInputRef = useRef();

  useEffect(() => {
    document.body.classList.remove("initial-load"); // make page scrollable in case App.js made it non-scrollable
    hideBootSplash();
    debugMessage("Mounted", "QuickStart");
    beginQuickStart();
  }, []);

  useEffect(() => {
    if (!loading) focusFirstInput();
  }, [loading]);

  useEffect(() => {
    if (stage !== "start") return undefined;
    const timeoutId = setTimeout(() => setShowStartEscape(true), 5000);
    return () => clearTimeout(timeoutId);
  }, [stage]);

  function leaveQuickStart() {
    window.location.replace("/home");
  }

  function abortQuickStart(reason = "none") {
    debugMessage(`Aborting quick start for reason: ${reason}`, "QuickStart");
    clearStoredAction();
    leaveQuickStart();
  }

  function proceedToNewAccount() {
    setStage("new-account");
    setLoading(false);
  }

  function proceedToAddProduct(units) {
    setExistingUnits(units || []);
    setStage("add-product");
    setLoading(false);
  }

  function proceedToCannotAdd(content) {
    setMessage(
      content
        ? { negative: true, content }
        : undefined,
    );
    setStage("cannot-add");
    setLoading(false);
  }

  function beginQuickStart() {
    const token = authenticator.getToken();
    const email = token?.decoded?.email || userEmail;

    if (action.action || action.configId) {
      writeStoredAction(action);
    }

    if (!token?.jwt) {
      debugMessage("No token, authenticator.signIn()", "QuickStart");
      authenticator.signIn();
      return;
    }

    if (isStaffEmail(email) && !testMode) {
      abortQuickStart("staff email");
      return;
    }

    if (testMode) {
      proceedToNewAccount();
      return;
    }

    let settled = false;
    const finish = (next) => {
      if (settled) return;
      settled = true;
      next();
    };

    const timeoutId = setTimeout(() => {
      debugMessage("Auth check timed out, showing new-account form", "QuickStart");
      finish(proceedToNewAccount);
    }, 4000);

    ajax
      .fetchUserAuth(token)
      .then((response) => {
        clearTimeout(timeoutId);
        if (
          response?.unauthorized === true ||
          response?.errorMessage === "pv-email-not-known" ||
          response?.errorMessage ||
          !response?.data
        ) {
          finish(proceedToNewAccount);
          return;
        }

        const { units = [], user = {} } = response.data;
        if (user.isStaff === true || user.isInstaller === true) {
          finish(() => abortQuickStart("isStaff or isInstaller true"));
          return;
        }

        finish(() => {
          const stage = chooseQuickStartStage({
            authData: { user, units },
            configId,
          });
          if (stage === "finish") {
            setExistingUnits(units);
            gotoStage("finish");
            setLoading(false);
            return;
          }
          if (stage === "add-product") {
            proceedToAddProduct(units);
            return;
          }
          proceedToNewAccount();
        });
      })
      .catch((err) => {
        debugMessage(err, "QuickStart");
        clearTimeout(timeoutId);
        finish(proceedToNewAccount);
      });
  }

  function submit() {
    setLoading(true);
    setMessage();

    if (testMode) {
      debugMessage(`testMode = true, dumping POST below:`, "QuickStart");
      debugMessage(
        {
          configId,
          hash,
          newAccountForm,
          addProductForm,
        },
        "QuickStart",
      );
      setLoading(false);
      return;
    }

    ajax
      .putRaw(authenticator.getToken(), `quickStart`, {
        configId,
        hash,
        newAccountForm,
        addProductForm,
      })
      .then((response) => {
        const { result, errorMessage } = response;

        if (result === "success") {
          gotoStage("finish");
          setLoading(false);
          return;
        }

        if (errorMessage === "asset-locked") {
          proceedToCannotAdd("This unit is locked. It cannot be changed.");
          return;
        }

        proceedToCannotAdd(
          getEnglishText(
            errorMessage === "invalid-hash"
              ? "quick-start : could-not-add-description"
              : "quick-start : something-went-wrong",
          ),
        );
      })
      .catch(() => {
        proceedToCannotAdd(
          getEnglishText("quick-start : something-went-wrong"),
        );
      });
  }

  function finish() {
    clearStoredAction();

    debugMessage("Finishing quick start", "QuickStart");
    leaveQuickStart();
  }

  function focusFirstInput() {
    try {
      newAccountInputRef.current.focus();
    } catch (err) {}
    try {
      addProductInputRef.current.focus();
    } catch (err) {}
  }

  function formIsValid() {
    return true;
  }

  function gotoStage(stage) {
    setStage(stage);
    setTimeout(focusFirstInput, 1);
  }

  function completeNewAccountStage() {
    function reject(name) {
      setMessage({
        error: true,
        content: (
          <>
            <strong>{name}</strong>&nbsp;&nbsp;
            {getEnglishText("quick-start : value-is-invalid")}.
          </>
        ),
      });
      return;
    }

    if (!newAccountForm["title"])
      return reject(getEnglishText("quick-start : title"));
    if (!newAccountForm["firstName"])
      return reject(getEnglishText("quick-start : first-name"));
    if (!newAccountForm["lastName"])
      return reject(getEnglishText("quick-start : last-name"));
    if (
      !newAccountForm["mobilePhoneNumber"] ||
      !phone(newAccountForm["mobilePhoneNumber"], { country: "GB" }).isValid
    )
      return reject(getEnglishText("quick-start : mobile-phone-number"));

    if (!newAccountForm["addressLine1"])
      return reject(getEnglishText("quick-start : address-line-1"));
    if (!newAccountForm["addressCityTown"])
      return reject(getEnglishText("quick-start : city-town"));
    if (
      !newAccountForm["postcode"] ||
      !postcodeValidator(newAccountForm["postcode"], "GB")
    )
      return reject(getEnglishText("quick-start : postcode"));

    if (
      newAccountForm["additionalEmail1"] &&
      !isValidEmailAddress(newAccountForm["additionalEmail1"])
    )
      return reject("Additional Email 1");
    if (
      newAccountForm["additionalEmail2"] &&
      !isValidEmailAddress(newAccountForm["additionalEmail2"])
    )
      return reject("Additional Email 2");
    if (
      newAccountForm["additionalEmail3"] &&
      !isValidEmailAddress(newAccountForm["additionalEmail3"])
    )
      return reject("Additional Email 3");

    // form is valid
    setMessage();
    gotoStage("add-product");
  }

  function completeAddProductStage() {
    // all fields are currently optional
    // form is valid
    setMessage();
    submit();
  }

  function onInputChange(e) {
    switch (stage) {
      case "new-account":
        setNewAccountForm({
          ...newAccountForm,
          [e.currentTarget.id]: e.currentTarget.value,
        });
        break;
      case "add-product":
        setAddProductForm({
          ...addProductForm,
          [e.currentTarget.id]: e.currentTarget.value,
        });
        break;
      default:
        return;
    }
  }

  function onDropdownChange(e, { id, value }) {
    switch (stage) {
      case "new-account":
        setNewAccountForm({ ...newAccountForm, [id]: value });
        break;
      case "add-product":
        setAddProductForm({ ...addProductForm, [id]: value });
        break;
      default:
        return;
    }
  }

  return (
    <div id="QuickStart">
      <Container>
        <Divider hidden />

        <Divider hidden />

        {/*********/}
        {/* START */}
        {/*********/}

        {stage === "start" && (
          <>
            <Header as="h1" inverted>
              Getting things ready ...
            </Header>
            <Segment loading={loading}>
              <Divider hidden />
              {message && <Message {...message} />}
              <Divider hidden />
              <Divider hidden />
            </Segment>
            {showStartEscape && (
              <>
                <Divider hidden />
                <div style={{ textAlign: "right" }}>
                  <Button inverted onClick={finish}>
                    {getEnglishText("quick-start : continue-without-adding")}
                  </Button>
                </div>
              </>
            )}
          </>
        )}

        {/***************/}
        {/* NEW ACCOUNT */}
        {/***************/}

        {stage === "new-account" && (
          <>
            <Header as="h1" inverted>
              {getEnglishText("quick-start : new-account")}
              <Header.Subheader>
                {getEnglishText("quick-start : create-your-new-portal-account")}
              </Header.Subheader>
            </Header>
            <Segment padded>
              <Form>
                <Header as="h2">
                  {getEnglishText("quick-start : about-you")}
                </Header>

                <Form.Group widths="equal">
                  <Form.Select
                    width={3}
                    fluid
                    id="title"
                    label="Title"
                    onChange={onDropdownChange}
                    value={newAccountForm["title"]}
                    options={[
                      { key: "mr", text: "Mr", value: "Mr" },
                      { key: "mrs", text: "Mrs", value: "Mrs" },
                      { key: "miss", text: "Miss", value: "Miss" },
                      { key: "ms", text: "Ms", value: "Ms" },
                      { key: "dr", text: "Dr", value: "Dr" },
                    ]}
                  />

                  <Form.Field>
                    <label>{getEnglishText("quick-start : first-name")}</label>
                    <input
                      id="firstName"
                      disabled={loading}
                      ref={newAccountInputRef}
                      // placeholder={getEnglishText('quick-start : first-name')}
                      onChange={onInputChange}
                      value={newAccountForm["firstName"]}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>{getEnglishText("quick-start : last-name")}</label>
                    <input
                      id="lastName"
                      disabled={loading}
                      // placeholder={getEnglishText('quick-start : last-name')}
                      onChange={onInputChange}
                      value={newAccountForm["lastName"]}
                    />
                  </Form.Field>
                </Form.Group>

                <Form.Field>
                  <label>
                    {getEnglishText("quick-start : mobile-phone-number")}
                    &nbsp;&nbsp;
                    <span style={{ opacity: 0.4 }}>(For SMS alerts)</span>
                  </label>
                  <input
                    id="mobilePhoneNumber"
                    disabled={loading}
                    // placeholder={getEnglishText('quick-start : mobile-phone-number')}
                    onChange={onInputChange}
                    value={newAccountForm["mobilePhoneNumber"]}
                  />
                </Form.Field>

                <Header as="h2">
                  {getEnglishText("quick-start : install-location")}
                </Header>
                <Form.Field>
                  <label>
                    {getEnglishText("quick-start : address-line-1")}
                  </label>
                  <input
                    id="addressLine1"
                    disabled={loading}
                    // placeholder={getEnglishText('quick-start : address-line-1')}
                    onChange={onInputChange}
                    value={newAccountForm["addressLine1"]}
                  />
                </Form.Field>
                <Form.Field>
                  <label>
                    {getEnglishText("quick-start : address-line-2")}
                  </label>
                  <input
                    id="addressLine2"
                    disabled={loading}
                    // placeholder={getEnglishText('quick-start : address-line-2')}
                    onChange={onInputChange}
                    value={newAccountForm["addressLine2"]}
                  />
                </Form.Field>
                <Form.Field>
                  <label>{getEnglishText("quick-start : city-town")}</label>
                  <input
                    id="addressCityTown"
                    disabled={loading}
                    // placeholder={getEnglishText('quick-start : city-town')}
                    onChange={onInputChange}
                    value={newAccountForm["addressCityTown"]}
                  />
                </Form.Field>
                <Form.Field>
                  <label>{getEnglishText("quick-start : postcode")}</label>
                  <input
                    id="postcode"
                    disabled={loading}
                    // placeholder={getEnglishText('quick-start : postcode')}
                    onChange={onInputChange}
                    value={newAccountForm["postcode"]}
                  />
                </Form.Field>

                <Header as="h2">
                  {getEnglishText("quick-start : additional-users")}
                </Header>
                {getEnglishText("quick-start : additional-users-description")}

                <Form.Field>
                  <label>
                    Additional Email 1&nbsp;&nbsp;
                    <span style={{ opacity: 0.4 }}>(Optional)</span>
                  </label>
                  <input
                    id="additionalEmail1"
                    disabled={loading}
                    placeholder={getEnglishText(
                      "quick-start : additional-email",
                    )}
                    onChange={onInputChange}
                    value={newAccountForm["additionalEmail1"]}
                  />
                </Form.Field>
                <Form.Field>
                  <label>
                    Additional Email 2&nbsp;&nbsp;
                    <span style={{ opacity: 0.4 }}>(Optional)</span>
                  </label>
                  <input
                    id="additionalEmail2"
                    disabled={loading}
                    placeholder={getEnglishText(
                      "quick-start : additional-email",
                    )}
                    onChange={onInputChange}
                    value={newAccountForm["additionalEmail2"]}
                  />
                </Form.Field>
                <Form.Field>
                  <label>
                    Additional Email 3&nbsp;&nbsp;
                    <span style={{ opacity: 0.4 }}>(Optional)</span>
                  </label>
                  <input
                    id="additionalEmail3"
                    disabled={loading}
                    placeholder={getEnglishText(
                      "quick-start : additional-email",
                    )}
                    onChange={onInputChange}
                    value={newAccountForm["additionalEmail3"]}
                  />
                </Form.Field>
              </Form>
              <Divider hidden />
              {message && <Message {...message} />}
              <div style={{ textAlign: "right" }}>
                <Button
                  labelPosition="right"
                  icon
                  primary
                  disabled={!formIsValid()}
                  onClick={completeNewAccountStage}
                >
                  <Icon name="arrow right" />
                  {getEnglishText("common : continue")}
                </Button>
              </div>
            </Segment>
          </>
        )}

        {/***************/}
        {/* ADD PRODUCT */}
        {/***************/}

        {stage === "add-product" && (
          <>
            <Header as="h1" inverted>
              {getEnglishText("quick-start : add-product")}
              <Header.Subheader>
                {getEnglishText("quick-start : add-product-description")}
              </Header.Subheader>
            </Header>
            <Segment padded>
              <Form>
                <Header as="h2">
                  {getEnglishText("quick-start : personalise")}
                </Header>
                {getEnglishText("quick-start : personalise-description")}
                <Form.Field>
                  <label>{getEnglishText("quick-start : custom-name")}</label>
                  <input
                    id="customName"
                    disabled={loading}
                    ref={addProductInputRef}
                    placeholder={configId}
                    onChange={onInputChange}
                    value={addProductForm["customName"]}
                  />
                </Form.Field>
                <Divider hidden />
              </Form>
              <Divider hidden />
              {message && <Message {...message} />}
              <div style={{ textAlign: "right" }}>
                {!existingUnits.length && (
                  <>
                    <Button
                      labelPosition="left"
                      icon
                      disabled={!formIsValid() || loading}
                      loading={loading}
                      onClick={() => gotoStage("new-account")}
                    >
                      <Icon name="arrow left" />
                      {getEnglishText("common : back")}
                    </Button>
                    &nbsp;&nbsp;
                  </>
                )}
                <Button
                  labelPosition="right"
                  icon
                  positive
                  disabled={!formIsValid() || loading}
                  loading={loading}
                  onClick={completeAddProductStage}
                >
                  <Icon name="check" />
                  {getEnglishText("common : finish")}
                </Button>
              </div>
            </Segment>
          </>
        )}

        {stage === "cannot-add" && (
          <>
            <Header as="h1" inverted>
              {getEnglishText("quick-start : could-not-add")}
              <Header.Subheader>
                {getEnglishText("quick-start : could-not-add-description")}
              </Header.Subheader>
            </Header>
            <Segment padded>
              {message && <Message {...message} />}
              <Divider hidden />
              <center>
                <Button
                  size="large"
                  labelPosition="right"
                  icon
                  primary
                  onClick={finish}
                >
                  <Icon name="arrow right" />
                  {getEnglishText("quick-start : continue-without-adding")}
                </Button>
              </center>
            </Segment>
          </>
        )}

        {/**********/}
        {/* FINISH */}
        {/**********/}

        {stage === "finish" && (
          <>
            <Header as="h1" inverted>
              {getEnglishText("quick-start : all-done")}
              <Header.Subheader>
                {getEnglishText("quick-start : all-done-description")}
              </Header.Subheader>
            </Header>
            <Segment padded>
              <Divider hidden />
              <Divider hidden />
              <Divider hidden />
              <center>
                <Header as="h1">
                  {getEnglishText("quick-start : continue-to-portal")}
                </Header>
                <Divider hidden />
                <Button
                  size="large"
                  labelPosition="right"
                  icon
                  positive
                  onClick={finish}
                >
                  <Icon name="arrow right" />
                  {getEnglishText("common : continue")}
                </Button>
              </center>
              <Divider hidden />
              <Divider hidden />
            </Segment>
          </>
        )}
        <Divider hidden />
        <Divider hidden />
        <Divider hidden />
        <Divider hidden />
        <Divider hidden />
        <Divider hidden />
      </Container>
    </div>
  );
}

export { QuickStart };
